import GameModel from "../../GameModel";
import { Settings } from "../../Settings";
import Rand from "../../util/Rand";
import Totals from "../crew/Totals";
import GameTime from "../time/GameTime";
import Values from "../value/Values";
import Bonus from "./Bonus";
import BonusCreator from "./BonusCreator";
import GoldBonusCreator from "./GoldBonusCreator";
import OreBonusCreator from "./OreBonusCreator";
import UpgradeBonusCreator from "./UpgradeBonusCreator";

export default class BonusCollection extends GameModel {
    /**
     * @param {GameTime} gameTime
     * @param {Totals} totals
     * @param {Values} values
     */
    constructor(gameTime, totals, values) {
        super();

        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;
        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {Values}
         */
        this.values = values;
        /**
         * @constant
         * @type {Bonus[]}
         */
        this.availableBonuses = [];
        /**
         * @constant
         * @type {Bonus[]}
         */
        this.activeBonuses = [];

        /**
         * Increments every time a change is made to the collection (add/remove).
         * @type {number}
         */
        this.availableChangeCount = 0;
        /**
         * Increments every time a change is made to the collection (add/remove).
         * @type {number}
         */
        this.activeChangeCount = 0;

        /**
         * @constant
         * @type {BonusCreator[]}
         */
        this.bonusCreators = [];
        /**
         * @constant
         * @type {Object}
         */
        this.bonusCreatorById = {};

        /**
         * @type {number}
         */
        this.prevBonusTurn = -Settings.bonus.newBonusTurns;

        ///////////////////////////////////////
        // Create all the bonus creators here
        ///////////////////////////////////////

        this.addBonusCreator(new OreBonusCreator('oreBonus', this.gameTime, this.totals, this.values.bonusOreMultiplier));
        this.addBonusCreator(new UpgradeBonusCreator('upgradeBonus', this.gameTime, this.totals, this.values.bonusUpgradeMultiplier));
        this.addBonusCreator(new GoldBonusCreator('goldBonus', this.gameTime, this.totals, this.values.bonusGoldIncrement));
    }

    /**
     * @private
     * @param {BonusCreator} bonusCreator 
     */
    addBonusCreator(bonusCreator) {
        this.bonusCreators.push(bonusCreator);
        this.bonusCreatorById[bonusCreator.id] = bonusCreator;
    }

    /**
     * @param {Bonus} bonus 
     */
    add(bonus) {
        if (!bonus) {
            console.log('BonusCollection.add() null bonus');
            return;
        }
        this.availableBonuses.push(bonus);
        this.availableChangeCount++;
    }

    /**
     * @param {Bonus} bonus 
     */
    addActiveBonusFromSave(bonus) {
        if (!bonus) {
            console.log('BonusCollection.addActiveBonusFromSave() null bonus');
            return;
        }
        this.activeBonuses.push(bonus);
        this.activeChangeCount++;

        console.log('********** addActiveBonusFromSave() ADDED ACTIVE. len=' + this.activeBonuses.length)
    }

    /**
     * @param {Bonus} bonus 
     */
    activateBonus(bonus) {
        console.log('****************** activatebonus ' + bonus.id + '   current length: ' + this.activeBonuses.length);

        if (bonus.activated) {
            console.log('BonusCollection.activateBonus() bonus already activated.');
            return;
        }

        if (this.remove(bonus)) {

            bonus.onPurchase();

            console.log('********** activateBonus() BONUS IS ACTIVATED: ' + bonus.activated);

            //////bonus.onActivation();
            this.activeBonuses.push(bonus);
            this.activeChangeCount++;

            console.log('********** activateBonus() ADDED ACTIVE. len=' + this.activeBonuses.length)
        }
        else {
            console.log('**************** failed to add active');
        }
    }

    /**
     * @param {Bonus} bonus 
     */
    remove(bonus) {
        if (!bonus) {
            console.log('BonusCollection.remove() bonus null');
            return false;
        }

        const index = this.availableBonuses.indexOf(bonus);
        if (index === -1) {
            console.log('BonusCollection.remove() did not find bonus in list');
            return false;
        }

        this.availableBonuses.splice(index, 1);
        this.availableChangeCount++;
        return true;
    }

    /**
     */
    updateForTurn() {
        for (let i = this.activeBonuses.length - 1; i >= 0; i--) {
            const bonus = this.activeBonuses[i];
            bonus.updateForTurn();

            if (!bonus.activated) {
                this.activeBonuses.splice(i, 1);
                this.activeChangeCount++;
            }
        }

        // See if it's time to add a new bonus to the avilable list
        const elapsedTurns = this.gameTime.turnNumber - this.prevBonusTurn;
        if (elapsedTurns >= Settings.bonus.newBonusTurns) {
            this.prevBonusTurn = this.gameTime.turnNumber;

            // TODO add logic to prevent duplicate bonus types
            const bonus = this.createRandomBonus();
            this.add(bonus);
        }
    }

    /**
     * @returns {Bonus}
     */
    createRandomBonus() {
        // pick random id that isn't present
        // create bonus
        const index = Rand.randomInt(this.bonusCreators.length);
        const bonusCreator = this.bonusCreators[index];

        return bonusCreator.createBonus();
    }

    /**
     * @param {string} creatorId 
     * @param {number} activationValue
     * @returns {Bonus}
     */
    createBonusFromSave(creatorId, activationValue) {
        /**
         * @type {BonusCreator}
         */
        const bonusCreator = this.bonusCreatorById[creatorId];
        if (!bonusCreator) {
            console.log('BonusCollection.createBonusById() Failed to find creator by id: ' + creatorId);
            return null;
        }

        return bonusCreator.createBonusFromSave(activationValue);
    }

    /**
     * @param {Bonus} bonus 
     * @returns {boolean}
     */
    purchaseBonus(bonus) {
        if (!bonus) {
            console.log('BonusCollection.purchaseBonus() null bonus');
            return false;
        }

        if (!bonus.isPurchasable()) {
            console.log('BonusCollection.purchaseBonus() bonus not purchasable');
            return false;
        }

        const index = this.availableBonuses.indexOf(bonus);
        if (index < 0) {
            console.log('BonusCollection.purchaseBonus() failed to find bonus in available list');
            return false;
        }

        // bonus.onPurchase();

        this.activateBonus(bonus);

        // this.availableBonuses.splice(index, 1);
        // this.activeBonuses.push(bonus);
        // this.availableChangeCount++;
        // this.activeChangeCount++;

        return true;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {

        // Deactivate all the active bonuses.
        for (let i = 0; i < this.activeBonuses.length; i++) {
            this.activeBonuses[i].reset();
        }

        this.availableBonuses.length = 0;
        this.activeBonuses.length = 0;
        this.availableChangeCount = 0;
        this.activeChangeCount = 0;
        this.prevBonusTurn = -Settings.bonus.newBonusTurns;
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
}